import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Power, Send, Ban, RotateCcw, Eye } from "lucide-react";
import { Brand } from "@/components/Layout";
import { AdminUsers } from "@/components/AdminUsers";
import { AdminAccountCard } from "@/components/AdminAccountCard";
import { AdminBlog } from "@/components/blog/AdminBlog";
import { api, errMsg, tokens, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const Stat = ({ label, value, tone = "" }) => (
  <div className="h-full rounded-md border border-slate-200 bg-white p-4">
    <p className="label-caps">{label}</p>
    <p className={`mt-2 text-2xl font-semibold ${tone || "text-slate-900"}`}>{value}</p>
  </div>
);

const TABS = ["dashboard", "users", "blog", "economics", "recalls", "sms"];

export default function Admin() {
  useSeo({ title: "Admin", description: "Administration.", path: "/admin/dashboard", noindex: true });
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [recalls, setRecalls] = useState([]);
  const [logs, setLogs] = useState([]);
  const [econ, setEcon] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testMobile, setTestMobile] = useState("0412345678");
  const [rowMobile, setRowMobile] = useState({});
  const [testResults, setTestResults] = useState({});
  const [backfillYear, setBackfillYear] = useState("2025");
  const [welcome, setWelcome] = useState(null);
  const [welcomeText, setWelcomeText] = useState("");
  const [subscribedText, setSubscribedText] = useState("");
  const [referralText, setReferralText] = useState("");
  const [users, setUsers] = useState([]);
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r, l, e, w, us, ac] = await Promise.all([
        api.get("/admin/stats", true),
        api.get("/admin/recalls", true),
        api.get("/admin/sms", true),
        api.get("/admin/economics", true),
        api.get("/admin/welcome", true),
        api.get("/admin/users", true),
        api.get("/admin/account", true),
      ]);
      setStats(s); setRecalls(r); setLogs(l); setEcon(e);
      setWelcome(w); setWelcomeText(w.text); setUsers(us); setAccount(ac);
      setSubscribedText(w.subscribed_text || "");
      setReferralText(w.referral_text || "");
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) { tokens.admin.clear(); nav("/admin"); }
      else toast.error(errMsg(e));
    }
  }, [nav]);

  useEffect(() => { if (!tokens.admin.get()) { nav("/admin"); return; } load(); }, [load, nav]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg || "Done"); await load(); return r; }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendRecallTest = (r) => {
    const mobile = (rowMobile[r.id] ?? testMobile).trim();
    if (!mobile) { toast.error("Enter an Australian mobile number"); return; }
    if (stats?.twilio_configured && !window.confirm(`Send a real SMS for "${r.product_name}" to ${mobile}?`)) return;
    return act(async () => {
      const res = await api.post("/admin/test-sms", { mobile, recall_id: r.id, allergies: r.allergens || [] }, true);
      setTestResults((t) => ({ ...t, [r.id]: res }));
    }, "Test SMS processed");
  };

  if (!stats) return <div className="p-10 text-slate-500">Loading admin...</div>;

  const welcomeSegments = welcomeText.length <= 160 ? 1 : Math.ceil(welcomeText.length / 153);
  const subscribedSegments = subscribedText.length <= 160 ? 1 : Math.ceil(subscribedText.length / 153);

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="border-b border-slate-200 bg-white/90 px-5 py-3.5 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Brand compact />
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={() => act(load, "Refreshed")}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button data-testid="admin-signout" onClick={() => { tokens.admin.clear(); nav("/admin"); }}
              className="rounded-md px-3 py-2 text-sm text-slate-500">Sign out</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-md px-4 py-2 text-sm font-medium capitalize ${tab === t ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
              {t === "sms" ? "SMS log" : t}
            </button>
          ))}
        </div>

        {tab === "dashboard" && (
          <div className="space-y-6" data-testid="admin-dashboard">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label="Feed status" value={stats.feed_status} tone={stats.feed_status === "healthy" ? "text-emerald-600" : "text-amber-600"} />
              <Stat label="Last successful check" value={stats.last_successful_check ? new Date(stats.last_successful_check).toLocaleTimeString("en-AU") : "Never"} />
              <Stat label="Recalls today" value={stats.recalls_today} />
              <Stat label="Total recalls" value={stats.total_recalls} />
              <Stat label="SMS sent today" value={stats.sms_today} />
              <Stat label="Delivery rate" value={`${stats.delivery_rate}%`} />
              <Stat label="SMS failures" value={stats.sms_failures} tone={stats.sms_failures ? "text-red-700" : ""} />
              <Stat label="Avg segments" value={stats.avg_segments} tone={stats.avg_segments > 1 ? "text-red-700" : "text-emerald-600"} />
              <Stat label="Subscribers" value={stats.subscribers} />
              <Stat label="Paying subscribers" value={stats.paying_subscribers} tone="text-emerald-600" />
              <Stat label="Total users" value={stats.total_users} />
              <Stat label="Twilio" value={stats.twilio_configured ? "Live" : "Mocked"} tone={stats.twilio_configured ? "text-emerald-600" : "text-amber-600"} />
              <Stat label="Stripe" value={stats.stripe.mode === "live" ? "Live" : "Test"} tone={stats.stripe.mode === "live" ? "text-emerald-600" : "text-amber-600"} />
              <Stat label="Feed errors" value={stats.feed_errors} />
            </div>

            <div className="flex flex-wrap gap-3">
              <button data-testid="admin-feed-check" disabled={busy}
                onClick={() => act(() => api.post("/admin/feed-check", {}, true), "Feed check complete")}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white">
                <RefreshCw className="h-4 w-4" /> Trigger feed check
              </button>
              <button data-testid="admin-kill-switch" disabled={busy}
                onClick={() => act(() => api.post("/admin/kill-switch", {}, true), "Global SMS setting toggled")}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white ${stats.sms_globally_disabled ? "bg-emerald-600" : "bg-red-700"}`}>
                <Power className="h-4 w-4" /> {stats.sms_globally_disabled ? "Re-enable SMS sending" : "Disable SMS sending"}
              </button>
            </div>

            <AdminAccountCard account={account} onSaved={(r) => setAccount((a) => ({ ...a, email: r.email }))} />

            <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="welcome-sms-card">
              <h3 className="text-base font-medium text-slate-900">Welcome SMS for new signups</h3>
              <p className="mt-1 text-xs text-slate-500">
                Sent when someone finishes onboarding, immediately followed by a second SMS showing a
                real recent recall matched to their state and allergies.
              </p>
              <textarea
                data-testid="welcome-sms-text"
                value={welcomeText}
                onChange={(e) => setWelcomeText(e.target.value)}
                rows={3}
                className="mt-3 w-full rounded-md border border-slate-300 p-3 text-sm"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className={`text-xs font-semibold ${welcomeSegments > 1 ? "text-red-700" : "text-emerald-600"}`}
                  data-testid="welcome-sms-segments">
                  {welcomeText.length} characters / {welcomeSegments} SMS segment{welcomeSegments === 1 ? "" : "s"}
                </span>
                <button data-testid="welcome-sms-save" disabled={busy || !welcomeText.trim()}
                  onClick={() => act(async () => {
                    const w = await api.put("/admin/welcome", { text: welcomeText }, true);
                    setWelcome(w); setWelcomeText(w.text);
                  }, "Welcome SMS saved")}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  Save welcome message
                </button>
                {welcome && !welcome.is_default && (
                  <button data-testid="welcome-sms-reset" disabled={busy}
                    onClick={() => setWelcomeText(welcome.default_text)}
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Restore default wording
                  </button>
                )}
              </div>
              {welcomeSegments > 1 && (
                <p className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                  This costs {welcomeSegments} SMS credits per signup instead of 1. Trim it to 160 characters to halve the cost.
                </p>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="subscribed-sms-card">
              <h3 className="text-base font-medium text-slate-900">Subscription confirmation SMS</h3>
              <p className="mt-1 text-xs text-slate-500">
                Sent once when a payment succeeds and the subscription becomes active. Not resent on renewal.
              </p>
              <textarea
                data-testid="subscribed-sms-text"
                value={subscribedText}
                onChange={(e) => setSubscribedText(e.target.value)}
                rows={2}
                className="mt-3 w-full rounded-md border border-slate-300 p-3 text-sm"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className={`text-xs font-semibold ${subscribedSegments > 1 ? "text-red-700" : "text-emerald-600"}`}
                  data-testid="subscribed-sms-segments">
                  {subscribedText.length} characters / {subscribedSegments} SMS segment{subscribedSegments === 1 ? "" : "s"}
                </span>
                <button data-testid="subscribed-sms-save" disabled={busy || !subscribedText.trim()}
                  onClick={() => act(async () => {
                    const w = await api.put("/admin/welcome", { subscribed_text: subscribedText }, true);
                    setWelcome(w); setSubscribedText(w.subscribed_text);
                  }, "Subscription SMS saved")}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  Save subscription message
                </button>
                {welcome && !welcome.subscribed_is_default && (
                  <button data-testid="subscribed-sms-reset" disabled={busy}
                    onClick={() => setSubscribedText(welcome.subscribed_default_text)}
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Restore default wording
                  </button>
                )}
              </div>

              <h3 className="mt-6 border-t border-slate-100 pt-5 text-base font-medium text-slate-900">
                Referral invite SMS
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Sent straight after the confirmation, with their share link. Must include{" "}
                <code className="rounded bg-slate-100 px-1">{"{link}"}</code>.
              </p>
              <textarea
                data-testid="referral-sms-text"
                value={referralText}
                onChange={(e) => setReferralText(e.target.value)}
                rows={2}
                className="mt-3 w-full rounded-md border border-slate-300 p-3 text-sm"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold text-slate-600" data-testid="referral-sms-segments">
                  {referralText.length} characters plus about 55 for the link
                </span>
                <button data-testid="referral-sms-save" disabled={busy || !referralText.trim()}
                  onClick={() => act(async () => {
                    const w = await api.put("/admin/welcome", { referral_text: referralText }, true);
                    setWelcome(w); setReferralText(w.referral_text);
                  }, "Referral SMS saved")}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  Save referral message
                </button>
                {welcome && !welcome.referral_is_default && (
                  <button data-testid="referral-sms-reset" disabled={busy}
                    onClick={() => setReferralText(welcome.referral_default_text)}
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Restore default wording
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="admin-backfill-card">
              <h3 className="text-base font-medium text-slate-900">Import a year of historical recalls</h3>
              <p className="mt-1 text-xs text-slate-500">
                Scrapes the official FSANZ listing for that year and imports anything missing. Never sends SMS.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input data-testid="admin-backfill-year" value={backfillYear}
                  onChange={(e) => setBackfillYear(e.target.value)}
                  className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="2025" />
                <button data-testid="admin-run-backfill" disabled={busy}
                  onClick={() => act(() => api.post(`/admin/backfill?year=${backfillYear}`, {}, true), `Imported ${backfillYear}`)}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  <RefreshCw className="h-4 w-4" /> Import year
                </button>
                <span className="text-xs text-slate-500">Large years can take a couple of minutes.</span>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="stripe-status">
              <h3 className="text-base font-medium text-slate-900">Payments</h3>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
                  stats.stripe.mode === "live"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="stripe-mode">
                  Stripe {stats.stripe.mode} mode
                </span>
                <span className="font-mono text-xs text-slate-600">{stats.stripe.account_id || "no account id"}</span>
                <span className="text-xs text-slate-500">
                  Webhooks: {stats.stripe.webhook_configured ? "signing secret set" : "signing secret missing"}
                </span>
              </div>
              {stats.stripe.problems?.length > 0 && (
                <ul className="mt-3 space-y-1 rounded-md bg-amber-50 p-3 text-xs text-amber-800" data-testid="stripe-problems">
                  {stats.stripe.problems.map((p) => <li key={p}>- {p}</li>)}
                </ul>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-5">
              <h3 className="text-base font-medium text-slate-900">Send a test SMS</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <input data-testid="admin-test-mobile" value={testMobile} onChange={(e) => setTestMobile(e.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="0412 345 678" />
                <button data-testid="admin-send-test-sms" disabled={busy}
                  onClick={() => act(async () => {
                    const r = await api.post("/admin/test-sms", { mobile: testMobile, allergies: ["Peanut"] }, true);
                    setPreview({ personalised: r, generic: null });
                  }, "Test SMS processed")}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                  <Send className="h-4 w-4" /> Send test
                </button>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="sender-config">
              <h3 className="text-base font-medium text-slate-900">SMS sender</h3>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
                  stats.sender.mode === "alphanumeric"
                    ? "border-amber-200 bg-amber-50 text-amber-700"
                    : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                  {stats.sender.mode === "alphanumeric" ? "Branded sender ID" : "Australian number"}
                </span>
                <span className="font-mono text-sm text-slate-900" data-testid="sender-value">{stats.sender.sender || "not set"}</span>
                <span className="text-xs text-slate-500">
                  Reply STOP handling: {stats.sender.two_way_stop ? "enabled" : "not available"}
                </span>
              </div>
              {stats.sender.problems?.length > 0 && stats.sender.mode === "alphanumeric" && (
                <p className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="sender-problems">
                  Invalid Sender ID: {stats.sender.problems.join("; ")}
                </p>
              )}
              {stats.sender.warnings?.map((w) => (
                <p key={w} className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">{w}</p>
              ))}
            </div>

            {stats.admin_alerts?.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-5" data-testid="admin-alerts">
                <h3 className="font-medium text-red-800">Administrator alerts</h3>
                <ul className="mt-2 space-y-1 text-sm text-red-700">
                  {stats.admin_alerts.map((a) => <li key={a.id}>{new Date(a.at).toLocaleString("en-AU")}: {a.message}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === "users" && (
          <AdminUsers
            users={users}
            busy={busy}
            onDelete={(u) => {
              if (!window.confirm(`Permanently delete ${u.mobile_number} from the database? This cannot be undone.`)) return;
              act(() => api.del(`/admin/users/${u.id}`, true), "User deleted");
            }}
          />
        )}

        {tab === "blog" && <AdminBlog />}

        {tab === "economics" && econ && (
          <div className="space-y-6" data-testid="admin-economics">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label={`MRR (${econ.currency})`} value={`$${econ.revenue.mrr.toFixed(2)}`} tone="text-emerald-600" />
              <Stat label="SMS spend / 30d" value={`$${econ.sms.spend_30d.toFixed(2)}`} tone="text-red-700" />
              <Stat
                label="Monthly margin"
                value={`$${econ.margin.monthly_margin.toFixed(2)}`}
                tone={econ.margin.monthly_margin >= 0 ? "text-emerald-600" : "text-red-700"}
              />
              <Stat
                label="Margin"
                value={econ.margin.margin_pct === null ? "-" : `${econ.margin.margin_pct}%`}
                tone={econ.margin.margin_pct >= 0 ? "text-emerald-600" : "text-red-700"}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-white p-5">
                <h3 className="text-base font-medium text-slate-900">SMS cost</h3>
                <p className="mt-1 text-xs text-slate-500" data-testid="pricing-basis">
                  Based on {econ.sms.pricing_basis} ({econ.sms.cost_per_segment.toFixed(4)} per segment)
                </p>
                <dl className="mt-4 space-y-2 text-sm">
                  {[
                    ["Billable messages", econ.sms.billable_messages],
                    ["Recall alerts", econ.sms.alert_messages],
                    ["Verification codes", econ.sms.verification_messages],
                    ["Mocked or failed (free)", econ.sms.mocked_or_failed],
                    ["Total segments", econ.sms.total_segments],
                    ["Average segments", econ.sms.avg_segments],
                    ["Cost per alert", `$${econ.sms.cost_per_alert.toFixed(4)}`],
                    ["Spend all time", `$${econ.sms.spend_total.toFixed(2)}`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 pb-1.5">
                      <dt className="text-slate-600">{k}</dt>
                      <dd className="font-medium text-slate-900">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-md border border-slate-200 bg-white p-5">
                <h3 className="text-base font-medium text-slate-900">Subscription revenue</h3>
                <p className="mt-1 text-xs text-slate-500">Live from Stripe</p>
                <dl className="mt-4 space-y-2 text-sm">
                  {[
                    ["Active subscriptions", econ.revenue.active_subscriptions],
                    ["MRR", `$${econ.revenue.mrr.toFixed(2)}`],
                    ["Annualised", `$${econ.revenue.arr.toFixed(2)}`],
                    ["Revenue per subscriber", `$${econ.revenue.arpu.toFixed(2)}`],
                    ["Collected to date", `$${econ.revenue.lifetime_collected.toFixed(2)}`],
                    ["SMS cost per subscriber", `$${econ.margin.sms_cost_per_subscriber_month.toFixed(4)}`],
                    ["Alerts per subscriber", econ.margin.alerts_per_subscriber],
                    ["Alerts affordable per subscriber", econ.margin.alerts_affordable_per_subscriber ?? "-"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 pb-1.5">
                      <dt className="text-slate-600">{k}</dt>
                      <dd className="font-medium text-slate-900">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {Object.keys(econ.revenue.by_plan).length > 0 && (
              <div className="rounded-md border border-slate-200 bg-white p-5">
                <h3 className="text-base font-medium text-slate-900">By plan</h3>
                <table className="mt-3 w-full text-sm">
                  <thead className="text-left text-slate-500">
                    <tr>{["Plan", "Subscribers", "MRR"].map((h) => <th key={h} className="pb-2 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {Object.entries(econ.revenue.by_plan).map(([plan, v]) => (
                      <tr key={plan} className="border-t border-slate-100">
                        <td className="py-2">{plan}</td>
                        <td className="py-2">{v.subscribers}</td>
                        <td className="py-2">${v.mrr.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="refresh-sms-costs"
                disabled={busy}
                onClick={() => act(() => api.post("/admin/refresh-sms-costs", {}, true), "Pulled latest Twilio pricing")}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
              >
                <RefreshCw className="h-4 w-4" /> Pull actual Twilio pricing
              </button>
              <span className="text-xs text-slate-500">
                {econ.sms.messages_with_actual_price} of {econ.sms.billable_messages} messages have confirmed pricing.
                Twilio publishes a price a few minutes after delivery.
              </span>
            </div>
          </div>
        )}

        {tab === "recalls" && (
          <div className="space-y-4" data-testid="admin-recalls">
            {recalls.map((r) => (
              <div key={r.id} className="rounded-md border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="label-caps">FSANZ title</p>
                    <p className="font-medium text-slate-900">{r.source_title}</p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`admin-preview-${r.id}`} disabled={busy}
                      onClick={() => act(async () => setPreview(await api.get(`/admin/recalls/${r.id}/preview?allergy=Peanut`, true)), "Preview generated")}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-medium">
                      <Eye className="h-3.5 w-3.5" /> Preview SMS
                    </button>
                    <button data-testid={`admin-reprocess-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/admin/recalls/${r.id}/reprocess`, {}, true), "Recall reprocessed")}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-medium">
                      <RotateCcw className="h-3.5 w-3.5" /> Reprocess
                    </button>
                    <button data-testid={`admin-block-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/admin/recalls/${r.id}/block`, {}, true), "Distribution setting changed")}
                      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white ${r.blocked ? "bg-emerald-600" : "bg-red-700"}`}>
                      <Ban className="h-3.5 w-3.5" /> {r.blocked ? "Unblock" : "Block"}
                    </button>
                  </div>
                </div>
                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div><dt className="label-caps">Product</dt><dd>{r.product_name} {r.product_size}</dd></div>
                  <div><dt className="label-caps">Type / reason</dt><dd>{r.recall_type}: {r.short_reason}</dd></div>
                  <div><dt className="label-caps">Distribution</dt><dd>{r.location_label} ({r.distribution_original})</dd></div>
                  <div><dt className="label-caps">Allergens</dt><dd>{r.allergens.join(", ") || "-"}</dd></div>
                  <div><dt className="label-caps">Version</dt><dd>v{r.version} {r.is_update && "(update)"}</dd></div>
                  <div><dt className="label-caps">Users notified</dt><dd data-testid={`admin-notified-${r.id}`}>{r.notified_count}</dd></div>
                  <div><dt className="label-caps">Status</dt><dd>{r.blocked ? "blocked" : r.status}</dd></div>
                  <div><dt className="label-caps">Source</dt>
                    <dd><a className="text-blue-700 underline" href={r.source_url} target="_blank" rel="noreferrer">FSANZ notice</a></dd></div>
                </dl>
                {r.validation_errors?.length > 0 && (
                  <p className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">In review: {r.validation_errors.join(", ")}</p>
                )}

                <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`admin-test-row-${r.id}`}>
                  <p className="label-caps">Send this recall as a test SMS</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      data-testid={`admin-test-mobile-${r.id}`}
                      value={rowMobile[r.id] ?? testMobile}
                      onChange={(e) => setRowMobile((m) => ({ ...m, [r.id]: e.target.value }))}
                      placeholder="0412 345 678"
                      className="w-44 rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                    <button
                      data-testid={`admin-send-test-${r.id}`}
                      disabled={busy}
                      onClick={() => sendRecallTest(r)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      <Send className="h-3.5 w-3.5" /> Send test SMS
                    </button>
                    <span className="text-xs text-slate-500">
                      Australian mobiles only. Personalised with {r.allergens.length ? r.allergens.join(", ") : "no allergen"}.
                      {stats.twilio_configured ? " Twilio is LIVE — a real SMS is charged and delivered." : " Twilio is mocked — nothing is delivered."}
                    </span>
                  </div>
                  {testResults[r.id] && (
                    <div className="mt-3 rounded-md bg-slate-50 p-3" data-testid={`admin-test-result-${r.id}`}>
                      <p className="sms-preview text-sm text-slate-900">{testResults[r.id].body}</p>
                      <p className="mt-1.5 text-xs text-slate-500">
                        {testResults[r.id].encoding} / {testResults[r.id].septets} septets /{" "}
                        <span className={testResults[r.id].segment_count > 1 ? "font-semibold text-red-700" : "font-semibold text-emerald-600"}>
                          {testResults[r.id].segment_count} segment(s)
                        </span>{" "}
                        / status {testResults[r.id].status}
                        {testResults[r.id].mocked && " (mocked)"}
                      </p>
                      {testResults[r.id].error && (
                        <p className="mt-1 text-xs font-medium text-red-700">Twilio error: {testResults[r.id].error}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "sms" && (
          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white" data-testid="admin-sms-log">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{["Recipient", "Recall", "Message", "Enc", "Seg", "Status", "SID", "Failure"].map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold text-slate-600">{h}</th>))}</tr>
              </thead>
              <tbody>
                {logs.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{m.phone_number}</td>
                    <td className="px-3 py-2">{m.recall_title || m.template}</td>
                    <td className="px-3 py-2 max-w-md sms-preview text-xs">{m.message_body}</td>
                    <td className="px-3 py-2">{m.encoding}</td>
                    <td className={`px-3 py-2 font-semibold ${m.segment_count > 1 ? "text-red-700" : "text-emerald-600"}`}>{m.segment_count}</td>
                    <td className="px-3 py-2">{m.status}</td>
                    <td className="px-3 py-2 font-mono text-xs">{m.twilio_message_sid}</td>
                    <td className="px-3 py-2 text-xs text-red-700">{m.failure_reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {preview && (
          <div className="mt-6 rounded-md border border-slate-200 bg-white p-5" data-testid="admin-sms-preview">
            <h3 className="text-base font-medium text-slate-900">SMS preview</h3>
            {preview.generic && (
              <div className="mt-3">
                <p className="label-caps">Generic</p>
                <p className="sms-preview text-sm">{preview.generic.body}</p>
                <p className="text-xs text-slate-500">{preview.generic.encoding} / {preview.generic.septets} septets / {preview.generic.segment_count} segment(s)</p>
              </div>
            )}
            <div className="mt-3">
              <p className="label-caps">Personalised (peanut allergy)</p>
              <p className="sms-preview text-sm">{preview.personalised.body}</p>
              <p className="text-xs text-slate-500">
                {preview.personalised.encoding} / {preview.personalised.septets} septets / {preview.personalised.segment_count} segment(s)
                {preview.personalised.source && ` / ${preview.personalised.source}`}
              </p>
            </div>
            {preview.short_url && <p className="mt-2 text-xs text-slate-500">Short link: {preview.short_url}</p>}
          </div>
        )}
        <p className="mt-8 text-xs text-slate-500">Last feed check: {stats.last_check ? fmtDate(stats.last_check.checked_at) : "never"}</p>
      </div>
    </div>
  );
}
